import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { bindable, autoinject } from 'aurelia-framework';
import { noop } from 'app/shared/lib/noop';
import { Api, AppSession } from 'app/shared/lib/essette-api';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { ISessionTenant, ISessionLocation, ISessionUserContext } from 'app/shared/lib/session/session-types';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { State } from 'app/shared/lib/essette-site';
import { Toaster } from 'app/shared/lib/toaster';
import { compareObjects } from 'app/shared/lib/compare-objects';

@autoinject()
export class EssTenantSelectionModalCustomElement {

    @bindable() availableTenants: ISessionTenant[] = [];
    protected rememberCheckIn: boolean = false;
    protected selectedTenant: ISessionTenant | null = null;
    protected selectedLocation: ISessionLocation | null = null;
    protected selectedUserContext: ISessionUserContext | null = null;
    protected twoFactorVisible: boolean = false;
    protected twoFactorCode: string = '';
    protected trustClient: boolean = false;

    private readonly state = new State();

    // refs
    private $modal!: EssModalCustomElement;
    private resolve: (success: boolean) => void = noop;

    constructor(
        private readonly session: AppSession,
        private readonly api: Api,
        private readonly env: EnvironmentService,
        private readonly toaster: Toaster,
        private readonly logger: AppLogger
    ) {
        this.logger.source = this;
        this.logger.target = 'console';
    }

    public async open(): Promise<boolean> {
        // if opened while logged in as a user, refresh the session to get up to date available tenants and user contexts
        if(this.env?.user?.isLoggedIn) {
            await this.api.refreshSession();
        }
        // select current tenant, followed by sole tenant
        this.setInitialSelectedValuesForLogin();

        this.rememberCheckIn = this.session.getRememberedState().enabled;

        this.trustClient = false;
        this.twoFactorCode = '';
        this.twoFactorVisible = false;

        this.$modal.open();
        return new Promise<boolean>((resolve) => this.resolve = resolve);
    }

    protected cancel(): void {
        this.twoFactorCode = '';

        this.resolve(false);
        this.$modal.close();
        this.reset();
    }

    protected verify(): void {
        this.updateSession();
        this.checkIn();
    }

    protected confirm(): void {
        this.updateSession();

        // check if 2FA is required and has not already been verified
        if(this.selectedTenant && this.selectedTenant.twoFactorEnabled && !this.session.authenticationCode) {
            // request 2FA verification
            this.requestVerification();
        }
        else {
            // check in immediately
            this.checkIn();
        }
    }

    private updateSession(): void {
        const { selectedTenant, selectedUserContext, selectedLocation } = this;
        if(selectedTenant) {
            this.session.updateTenant(selectedTenant);
            if(selectedLocation) {
                this.session.updateLocation(selectedLocation);
            } else if(selectedTenant.locations.length == 0) {
                this.session.updateLocation(null);
            }
            if(selectedTenant.userContexts.length > 1) {
                if(selectedUserContext) {
                    this.session.updateUserContext(selectedUserContext);
                }
            } else {
                this.session.updateUserContext(selectedTenant.userContexts[0]);
            }
        }
    }

    private async requestVerification(): Promise<void> {
        try {
            this.state.beginLoad();
            this.twoFactorCode = await this.api.verifyLogin();
        }
        catch(e) {
            this.toaster.error().message('Unable to Verify Login.').show();
            this.logger.error(e);
        }
        finally {
            this.state.endLoad();
        }

        if(this.twoFactorCode) {
            await this.checkIn();
        }
        else {
            // show two factor verification info
            this.trustClient = false;
            this.twoFactorCode = '';
            this.twoFactorVisible = true;
        }
    }

    private async checkIn(): Promise<void> {
        try {
            this.state.beginLoad();
            await this.api.checkIn(this.twoFactorCode || this.session.authenticationCode || '', this.trustClient);
        }
        catch(e) {
            // clear session code if check in fails
            this.toaster.error().message('Unable to Check In.').show();
            this.session.updateAuthenticationCode('');
            this.logger.error(e);
            throw e;
        }
        finally {
            this.state.endLoad();
        }

        this.remember();
        this.complete();
    }

    private remember(): void {
        const { rememberCheckIn, selectedTenant, selectedUserContext, selectedLocation } = this;
        if(rememberCheckIn) {
            this.session.remember({
                enabled: true,
                tenant: selectedTenant && selectedTenant.code || '',
                location: selectedLocation && selectedLocation.code || '',
                context: selectedUserContext && selectedUserContext.id || ''
            });
        }
        else {
            this.session.remember({ enabled: false });
        }
    }

    private complete(): void {
        this.session.configureEnvironment();
        this.resolve(true);
        this.$modal.close();
        this.reset();
    }

    private reset(): void {
        this.selectedLocation = this.selectedTenant = this.selectedUserContext = null;
    }

    protected selectDefaultUserContextAndLocation(): void {
        const tenant = this.selectedTenant;
        if(!tenant) {
            this.selectedUserContext = null;
            this.selectedLocation = null;
        }
        else {
            if(tenant.userContexts) {
                this.selectedUserContext = tenant.userContexts[0] || null;
            }
            if(tenant.locations && tenant.locations.length === 1) {
                this.selectedLocation = tenant.locations[0];
            } else {
                this.selectedLocation = null;
            }
        }
    }

    private setInitialSelectedTenant() {
        if(this.env.tenant.isSelected) {
            this.selectedTenant = this.availableTenants.find(tenant => tenant.id === this.env.tenant.id) || null;
        }
        if(!this.selectedTenant && this.availableTenants.length === 1) {
            this.selectedTenant = this.availableTenants[0];
        }
    }

    private setInitialSelectedValuesForLogin() {
        this.setInitialSelectedTenant();
        this.selectDefaultUserContextAndLocation();
    }

    protected availableTenantsChanged(newValue: ISessionTenant[], oldValue: ISessionTenant[]): void {
        /**
         * Only react if there are actual changes to the available tenants array.
         *
         * We need to use the propertyFilter because the currently selected tenant is getting an extra undefined length property which is causing the compareObjects call to fail.
         *
         * There is still an issue when the available tenants data legitimately changes, i.e. new/updated call center, tfa policy changes, or when a user logs out of the app in a different browser tab.
         * In this case, if the user is not logged in, the page will reset and the user will not be able to proceed.
         */
        const propertyFilter = (propertyKey: string | number, target: any, propertyPath: string[]) => ["code", "description", "id", "isSelected", "language", "locations", "timeZone", "twoFactorEnabled", "twoFactorPersist", "userContexts"].includes(propertyKey.toString())
        const result = compareObjects(newValue, oldValue, { returnDifferences: false, propertyFilter });
        if(result.areEqual) {
            return;
        }
        if(this.env?.user?.isLoggedIn) {
            this.setInitialSelectedTenant();

            if(this.env.location.isSelected && this.selectedTenant && this.selectedTenant.locations) {
                this.selectedLocation = this.selectedTenant.locations.find(location => location.id === this.env.location.id) ??
                    (this.selectedTenant.locations ? this.selectedTenant.locations[0] : null);
            }

            if(this.env.context.isSelected && this.selectedTenant && this.selectedTenant.userContexts) {
                this.selectedUserContext = this.selectedTenant.userContexts.find(context => context.id === this.env.context.id) ??
                    (this.selectedTenant.userContexts ? this.selectedTenant.userContexts[0] : null);
            }
        }
        else {
            this.reset();
        }
    }
}
